<template>
  <Teleport to="body">
    <Transition name="drawer" appear>
      <div v-if="modelValue" class="drawer-overlay" @click="handleOverlayClick">
        <div :class="drawerClasses" @click.stop>
          <!-- 抽屉头部 -->
          <div v-if="$slots.header || title || closable" class="drawer-header">
            <slot name="header">
              <h3 v-if="title" class="drawer-title">{{ title }}</h3>
            </slot>
            <button
              v-if="closable"
              type="button"
              class="drawer-close"
              @click="handleClose"
              aria-label="关闭"
            >
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
          
          <!-- 抽屉内容 -->
          <div class="drawer-body">
            <slot />
          </div>
          
          <!-- 抽屉底部 -->
          <div v-if="$slots.footer" class="drawer-footer">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
export default {
  name: 'BaseDrawer',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    placement: {
      type: String,
      default: 'right',
      validator: (value) => ['left', 'right', 'top', 'bottom'].includes(value)
    },
    width: {
      type: String,
      default: '400px'
    },
    height: {
      type: String,
      default: '300px'
    },
    closable: {
      type: Boolean,
      default: true
    },
    closeOnOverlay: {
      type: Boolean,
      default: true
    },
    closeOnEscape: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue', 'close', 'open'],
  computed: {
    drawerClasses() {
      return [
        'drawer',
        `drawer--${this.placement}`,
        {
          'drawer--no-header': !this.$slots.header && !this.title,
          'drawer--no-footer': !this.$slots.footer
        }
      ]
    },
    drawerStyle() {
      const style = {}
      if (this.placement === 'left' || this.placement === 'right') {
        style.width = this.width
      } else {
        style.height = this.height
      }
      return style
    }
  },
  watch: {
    modelValue: {
      handler(newValue) {
        if (newValue) {
          this.handleOpen()
        } else {
          this.handleDrawerClose()
        }
      },
      immediate: true
    }
  },
  mounted() {
    if (this.closeOnEscape) {
      document.addEventListener('keydown', this.handleEscapeKey)
    }
  },
  beforeUnmount() {
    if (this.closeOnEscape) {
      document.removeEventListener('keydown', this.handleEscapeKey)
    }
  },
  methods: {
    handleClose() {
      this.$emit('update:modelValue', false)
      this.$emit('close')
    },
    handleOverlayClick() {
      if (this.closeOnOverlay) {
        this.handleClose()
      }
    },
    handleEscapeKey(event) {
      if (event.key === 'Escape' && this.modelValue) {
        this.handleClose()
      }
    },
    handleOpen() {
      document.body.style.overflow = 'hidden'
      this.$emit('open')
    },
    handleDrawerClose() {
      document.body.style.overflow = ''
    }
  }
}
</script>

<style scoped>
.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
}

.drawer {
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  max-height: 100vh;
  overflow: hidden;
}

.drawer--right {
  margin-left: auto;
  width: v-bind(width);
}

.drawer--left {
  margin-right: auto;
  width: v-bind(width);
}

.drawer--top {
  margin-bottom: auto;
  width: 100%;
  height: v-bind(height);
}

.drawer--bottom {
  margin-top: auto;
  width: 100%;
  height: v-bind(height);
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.drawer-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
}

.drawer-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.drawer-close:hover {
  background: #e5e7eb;
  color: #374151;
}

.drawer-body {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
}

.drawer-footer {
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

/* 动画效果 */
.drawer-enter-active,
.drawer-leave-active {
  transition: all 0.3s ease;
}

.drawer-enter-from .drawer--right,
.drawer-leave-to .drawer--right {
  transform: translateX(100%);
}

.drawer-enter-from .drawer--left,
.drawer-leave-to .drawer--left {
  transform: translateX(-100%);
}

.drawer-enter-from .drawer--top,
.drawer-leave-to .drawer--top {
  transform: translateY(-100%);
}

.drawer-enter-from .drawer--bottom,
.drawer-leave-to .drawer--bottom {
  transform: translateY(100%);
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}
</style>
